import { DEFAULT_SECTION_ACCESS_TYPE, SECTIONS, SECTION_ACCESS_TYPES } from './consts';
import {
    CUSTOM_ACCESS_TYPE,
    MANAGE_PERMISSIONS_STEPS,
    translationPrefix as translationRootPrefix,
} from '../teamManagement/utils';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

export const SCREEN_KEY = {
    PARTNER_CONFIG: 'new_partner_config',
    MANAGE_LINKS: 'manage_links',
};

export const SECTIONS_PAGES_CONFIG = [
    {
        step: MANAGE_PERMISSIONS_STEPS.attribution,
        page: {
            key: SCREEN_KEY.PARTNER_CONFIG,
            link: 'react/partner-config',
            translationKey: `${translationPrefix}.ATTRIBUTION.PAGE_DISPLAY_NAME`,
        },
        cards: [
            {
                title: `${translationPrefix}.ATTRIBUTION.TITLE`,
                description: `${translationPrefix}.ATTRIBUTION.DESCRIPTION`,
                parts: [
                    {
                        sectionNames: [SECTIONS.PARTNER_CONFIGURATION_CREDENTIALS],
                        tooltipImageKey: 'configuration_credentials',
                        tooltipDescriptionSectionName: SECTIONS.PARTNER_CONFIGURATION_CREDENTIALS,
                    },
                    {
                        displayName: `${translationPrefix}.ATTRIBUTION.ATTRIBUTION_POSTBACKS.DISPLAY_NAME`,
                        sectionNames: [
                            SECTIONS.PARTNER_CONFIGURATION_ATTRIBUTION_INSTALL_POSTBACKS,
                            SECTIONS.PARTNER_CONFIGURATION_ATTRIBUTION_REENGAGEMENT_POSTBACKS,
                        ],
                        tooltipImageKey: 'attribution_postbacks',
                    },
                    {
                        sectionNames: [SECTIONS.PARTNER_CONFIGURATION_ATTRIBUTION_WINDOW_SETTINGS],
                        tooltipImageKey: 'attribution_window_settings',
                    },
                    {
                        sectionNames: [SECTIONS.PARTNER_CONFIGURATION_IN_APP_EVENTS_POSTBACK],
                        tooltipImageKey: 'event_postbacks',
                    },
                ],
            },
        ],
    },
    {
        step: MANAGE_PERMISSIONS_STEPS.links,
        page: {
            key: SCREEN_KEY.MANAGE_LINKS,
            link: 'react/link-management',
            translationKey: `${translationPrefix}.LINKS.PAGE_DISPLAY_NAME`,
        },
        cards: [
            {
                title: `${translationPrefix}.LINKS.PARTNER_LINKS.TITLE`,
                description: `${translationPrefix}.LINKS.PARTNER_LINKS.DESCRIPTION`,
                parts: [
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_PARTNER_LINK_SETTINGS_AND_REDIRECTS],
                        tooltipImageKey: 'link_settings_and_redirects',
                    },
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_PARTNER_ATTRIBUTION_SETTINGS],
                        tooltipImageKey: 'link_attribution_settings',
                    },
                ],
            },
            {
                title: `${translationPrefix}.LINKS.CUSTOM_SOURCE_LINKS.TITLE`,
                description: `${translationPrefix}.LINKS.CUSTOM_SOURCE_LINKS.DESCRIPTION`,
                parts: [
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_CUSTOM_LINK_SETTINGS_AND_REDIRECTS],
                        tooltipImageKey: 'link_settings_and_redirects',
                    },
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_CUSTOM_ATTRIBUTION_SETTINGS],
                        tooltipImageKey: 'link_attribution_settings',
                    },
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_CUSTOM_OG_TAGS],
                        tooltipImageKey: 'link_og_tags',
                    },
                    {
                        sectionNames: [SECTIONS.MANAGE_LINKS_CUSTOM_LINK_PARAMETERS],
                        tooltipImageKey: 'link_parameters',
                    },
                ],
            },
        ],
    },
];

export const SECTION_PAGES_STEPS = SECTIONS_PAGES_CONFIG.map(({ step }) => step);

export const VIEW_ONLY_PAGES_MAP = {
    ...SECTIONS_PAGES_CONFIG.reduce((map, config) => {
        map[config.page.key] = config.cards.flatMap(card => card.parts.flatMap(part => part.sectionNames));
        return map;
    }, {}),
    singularevents: [SECTIONS.SINGULAR_EVENTS_MANAGE],
    conversion_models: [SECTIONS.CONVERSION_MODELS_MANAGE],
    audiences_segments: [SECTIONS.AUDIENCES_SEGMENTS_MANAGE],
    audiences_distributions: [SECTIONS.AUDIENCES_DISTRIBUTIONS_MANAGE],
    'custom-dimensions': [SECTIONS.CUSTOM_DIMENSIONS_MANAGE],
    alerts_v2: [SECTIONS.ALERTS_MANAGE],
    fraud_rules: [SECTIONS.FRAUD_RULES_MANAGE],
};

export const isScreenViewOnly = (screenName, sectionPermissions) => {
    const sections = VIEW_ONLY_PAGES_MAP[screenName];
    if (!sections) return false;
    const relevantPermissions = sectionPermissions.filter(sp => sections.includes(sp.name));
    return (
        relevantPermissions.length > 0 && relevantPermissions.every(sp => sp.access_type === SECTION_ACCESS_TYPES.VIEW)
    );
};

export const buildAccessTypesRecord = (sectionNames, sectionPermissions) =>
    sectionNames.reduce((acc, name) => {
        const permission = sectionPermissions?.find(p => p.name === name);
        acc[name] = permission ? permission.access_type : DEFAULT_SECTION_ACCESS_TYPE;
        return acc;
    }, {});

export const getPartAccessType = (part, accessTypesRecord) => {
    if (part.sectionNames.length >= 2) {
        const types = part.sectionNames.map(name => accessTypesRecord[name]);
        if (!types.every(t => t === types[0])) return CUSTOM_ACCESS_TYPE;
    }
    const isAllEdit = part.sectionNames.every(name => accessTypesRecord[name] === SECTION_ACCESS_TYPES.EDIT);
    return isAllEdit ? SECTION_ACCESS_TYPES.EDIT : SECTION_ACCESS_TYPES.VIEW;
};

export const buildPartDisplayName = (part, selectedAccessType, translate, specificSectionNames = []) => {
    let result = '';
    if (part.displayName) {
        result = translate(part.displayName);
    }
    if (!part.displayName || (part.displayName && selectedAccessType !== CUSTOM_ACCESS_TYPE)) {
        if (part.displayName) result += ' - ';
        part.sectionNames
            .filter(sectionName => !specificSectionNames.length || specificSectionNames.includes(sectionName))
            .forEach((sectionName, index) => {
                result += `${index > 0 ? ' & ' : ''}${translate(
                    `${translationPrefix}.SECTIONS.${sectionName}.DISPLAY_NAME`
                )}`;
            });
    }
    return result;
};
